import { isDetectedPlatform, stepAndBannerFromLookup, stepAndBannerFromPassword } from './outcomes'
import { INewSigninState, NewSigninAction, SigninBanner, SigninStep } from './types'

export const buildInitialState = (step: SigninStep, banner: SigninBanner = { kind: 'none' }): INewSigninState => ({
  step,
  banner,
  isBusy: false,
})

export const reducer = (state: INewSigninState, action: NewSigninAction): INewSigninState => {
  switch (action.type) {
    case 'lookupStarted':
    case 'passwordSubmitted':
    case 'platformSigninStarted':
    case 'mfaSubmitted':
    case 'resetSubmitted':
      return { ...state, banner: { kind: 'none' }, isBusy: true }

    // Asked for by a link with no address behind it, so it starts by collecting one.
    case 'resetStarted':
      return { step: { name: 'resetRequest', email: action.email }, banner: { kind: 'none' }, isBusy: false }

    case 'resetCodeSent':
      return {
        step: {
          name: 'resetCode',
          email: action.email,
          username: action.username,
          adpEnabled: action.adpEnabled,
        },
        banner: { kind: 'none' },
        isBusy: false,
      }

    case 'resetCodeResent':
      return { ...state, banner: { kind: 'codeResent' }, isBusy: false }

    // A code that never arrived cannot be retyped, so that one reason goes back to the address
    // rather than leaving the visitor staring at six boxes they can never fill.
    case 'resetFailed':
      return {
        step: action.reason === 'deliveryFailed' && 'email' in state.step ? { name: 'resetRequest', email: state.step.email ?? '' } : state.step,
        banner: { kind: 'resetFailed', reason: action.reason },
        isBusy: false,
      }

    // Cognito does not sign anyone in on a reset, so the new password still has to be used.
    // The step it lands on is the one that asks for it.
    case 'passwordResetCompleted':
      return {
        step:
          state.step.name === 'resetCode'
            ? {
                name: 'password',
                email: state.step.email,
                username: state.step.username,
                adpEnabled: state.step.adpEnabled,
              }
            : state.step,
        banner: { kind: 'passwordResetDone' },
        isBusy: false,
      }

    // The step stays put with its field freed up: a code is retyped, not navigated away from.
    case 'mfaCodeRejected':
      return {
        ...state,
        banner: { kind: action.isMismatch ? 'mfaCodeMismatch' : 'mfaFailed' },
        isBusy: false,
      }

    case 'lookupResolved': {
      if (action.outcome.kind === 'aborted') return state
      const { step, banner } = stepAndBannerFromLookup({
        outcome: action.outcome,
        email: action.email,
        currentStep: state.step,
      })
      return { step, banner, isBusy: false }
    }

    case 'passwordResolved': {
      const { step, banner } = stepAndBannerFromPassword({
        outcome: action.outcome,
        currentStep: state.step,
      })
      // The signed-in path navigates away, so it must keep the busy state to avoid a
      // flash of an enabled form behind the redirect.
      const isBusy = action.outcome.kind === 'signedIn'
      return { step, banner, isBusy }
    }

    // A step of its own rather than a modal over the form: it is where the sign-in ended. The
    // reason and the ask sit in the banner, as for every other account the page cannot open.
    case 'accessDenied':
      return {
        step: { name: 'noAccess', email: action.email, platform: action.platform },
        banner: { kind: 'noAccess', email: action.email, organizationName: action.organizationName },
        isBusy: false,
      }

    case 'inactiveAccountDetected':
      return {
        step: { name: 'email', email: action.email },
        banner: { kind: 'inactiveAccount', email: action.email, organizationName: action.organizationName },
        isBusy: false,
      }

    // Back to an empty field, from "Change" and from the focused step's way out alike.
    // Reaching the password or platform step at all means the address already resolved to an
    // account (by lookup or by the remembered marker), so asking to change it can only mean
    // "not this account". There is no typo left to correct: an address that failed to resolve
    // never leaves the email step in the first place.
    case 'restarted':
      return { step: { name: 'email', email: '' }, banner: { kind: 'none' }, isBusy: false }

    // An outcome with nothing to do but say so. The step stays where it is, and the form is
    // released because the attempt behind the message is over either way.
    case 'bannerRaised':
      return { ...state, banner: action.banner, isBusy: false }

    // The account the page opened on is gone, so it goes back to the address, which is where
    // every other "no account" answer lands.
    case 'platformAccountNotFound':
      return {
        step: { name: 'email', email: action.email },
        banner: { kind: 'noPlatformAccount', platform: action.platform },
        isBusy: false,
      }

    // The sign-in step for the organisation's platform already holds its button, so the banner
    // only says why the page moved there. A platform with no step here gets the address instead.
    case 'orgOnOtherPlatformDetected':
      return {
        step: isDetectedPlatform(action.platform)
          ? {
              name: 'platform',
              platform: action.platform,
              email: action.email,
              organizationName: action.organizationName,
              source: 'lookup',
            }
          : { name: 'email', email: action.email },
        banner: { kind: 'orgOnOtherPlatform', organizationName: action.organizationName },
        isBusy: false,
      }

    // The address resolved to several accounts and the visitor picked the email one, which is
    // the only choice this page can answer itself.
    case 'emailSigninChosen':
      return state.step.name === 'choosePlatform' && state.step.username
        ? {
            ...state,
            step: {
              name: 'password',
              email: state.step.email,
              username: state.step.username,
              adpEnabled: state.step.adpEnabled,
            },
          }
        : state

    case 'bannerCleared':
      return state.banner.kind === 'none' ? state : { ...state, banner: { kind: 'none' } }

    case 'busyChanged':
      return { ...state, isBusy: action.isBusy }

    default:
      return state
  }
}
